// ============ DINÂMICAS DAS ESTAÇÕES ============
// Sistema de tensões emocionais entre os 4 eixos raiz
// Cada eixo forte projeta uma sombra sobre os outros
// As duplas indicam integração ou distância; as triplas, convergência ou fragmentação

// Threshold para tensão significativa (diferença de score entre dois eixos)
export const TENSION_STRONG = 12;
export const TENSION_MODERATE = 8;

// ====== 6 PARES DE TENSÃO ENTRE EIXOS RAIZ ======
export const tensionPairs = [
  // ===== EIXOS OPOSTOS (tensão primária) =====
  {
    id: 'guardiao_peregrino',
    natureza: 'Corpo e Tempo',
    eixoA: { key: 'guardiao', name: 'Guardião' },
    eixoB: { key: 'peregrino', name: 'Peregrino' },
    isOpposite: true,
    whenAStronger: {
      titulo: 'As raízes que impedem o voo',
      sombra: 'Quando o Guardião se fortalece demais, a estabilidade que ele constrói pode se transformar em rigidez. O chão firme vira cimento — e o Peregrino interior, aquele que precisa de vento e horizonte, começa a sufocar. Você pode perceber isso como medo de mudança, apego excessivo à rotina, ou dificuldade de se abrir para o desconhecido.',
      regulacao: 'O caminho não é abandonar suas raízes, mas aprender que as árvores mais altas são também as que mais se curvam ao vento. Permita-se pequenos movimentos: mude um trajeto, experimente algo fora do plano, deixe uma certeza em aberto por um dia.',
      pratica: 'Escolha um dia por semana para fazer algo sem planejar. Saia sem destino, leia um livro aleatório, converse com alguém inesperado. Observe o que acontece quando o controle descansa.'
    },
    whenBStronger: {
      titulo: 'O vento que nunca pousa',
      sombra: 'Quando o Peregrino se fortalece demais, o movimento vira fuga disfarçada de busca. O horizonte é sempre o próximo, nunca este. O Guardião interior — aquele que sabe cuidar, manter e construir — começa a enfraquecer. Você pode perceber isso como inquietude constante, dificuldade de manter compromissos, ou uma sensação de que nada é suficiente.',
      regulacao: 'O caminho não é parar de caminhar, mas descobrir que ficar também é uma forma de viagem. Existem mundos inteiros dentro do que já existe. A quietude não é o oposto da aventura — é uma aventura diferente.',
      pratica: 'Dedique 15 minutos por dia a algo repetitivo e presente: cozinhar devagar, cuidar de uma planta, organizar um espaço. Observe a beleza de estar sem precisar ir.'
    }
  },
  {
    id: 'amante_alquimista',
    natureza: 'Sentimento e Sentido',
    eixoA: { key: 'amante', name: 'Amante' },
    eixoB: { key: 'alquimista', name: 'Alquimista' },
    isOpposite: true,
    whenAStronger: {
      titulo: 'O altar que se torna o outro',
      sombra: 'Quando o Amante se fortalece demais, o vínculo externo pode eclipsar a chama interna. O outro se torna o propósito, e o Alquimista — aquele que busca sentido próprio, transformação interior — começa a se apagar. Você pode perceber isso como perda de identidade nos relacionamentos, dificuldade de estar sozinho(a), ou sensação de vazio quando não há alguém por perto.',
      regulacao: 'O caminho não é amar menos, mas redescobrir que você também é uma fogueira — não apenas alguém que se aquece na fogueira do outro. O amor mais profundo nasce quando duas chamas se encontram, não quando uma se apaga para iluminar a outra.',
      pratica: 'Reserve um tempo semanal para algo que é só seu: uma prática, um estudo, uma criação. Algo que não envolva outra pessoa. Observe como se sente quando a presença é consigo mesmo(a).'
    },
    whenBStronger: {
      titulo: 'A profundidade que evita o toque',
      sombra: 'Quando o Alquimista se fortalece demais, a busca por sentido pode se transformar em isolamento. A sabedoria vira escudo, a reflexão substitui o gesto, e o Amante interior — aquele que precisa de presença, toque e entrega — começa a murchar. Você pode perceber isso como racionalização excessiva dos sentimentos, dificuldade de se entregar, ou sensação de que "ninguém entende".',
      regulacao: 'O caminho não é pensar menos, mas deixar que a sabedoria desça do pensamento para o corpo. Nem tudo precisa ser compreendido para ser vivido. Às vezes, o sentido mora no abraço que não tem explicação.',
      pratica: 'Permita-se um gesto de afeto por dia sem explicar por quê. Um abraço mais longo, uma mensagem carinhosa sem motivo, um olhar sustentado. Observe como o corpo responde quando o coração lidera.'
    }
  },

  // ===== EIXOS CRUZADOS (tensão secundária) =====
  {
    id: 'guardiao_amante',
    natureza: 'Corpo e Sentimento',
    eixoA: { key: 'guardiao', name: 'Guardião' },
    eixoB: { key: 'amante', name: 'Amante' },
    isOpposite: false,
    whenAStronger: {
      titulo: 'O muro onde deveria haver porta',
      sombra: 'Quando o Guardião domina sobre o Amante, a proteção que deveria servir ao amor passa a impedi-lo. O controle substitui a vulnerabilidade, os limites endurecem onde deveriam ser permeáveis. Você pode perceber isso como dificuldade de se abrir emocionalmente, tendência a "resolver" em vez de "sentir", ou relacionamentos funcionais mas sem profundidade.',
      regulacao: 'O caminho é aprender que a verdadeira proteção não é um muro — é uma porta que sabe quando abrir e quando fechar. A vulnerabilidade não é fraqueza do Guardião; é sua forma mais corajosa.',
      pratica: 'Compartilhe algo que você normalmente guardaria. Uma insegurança, um desejo, uma memória. Escolha alguém seguro e abra uma fresta. Observe que o céu não cai.'
    },
    whenBStronger: {
      titulo: 'O rio sem margens',
      sombra: 'Quando o Amante domina sobre o Guardião, os sentimentos transbordam sem contenção. O afeto perde forma, os limites se dissolvem, e a estrutura que sustenta a vida prática começa a se fragilizar. Você pode perceber isso como dificuldade de dizer não, negligência com o corpo e rotinas, ou tendência a se perder emocionalmente nos outros.',
      regulacao: 'O caminho é entender que o amor precisa de margens para não se perder. Os limites não são prisões do sentimento — são a moldura que transforma uma mancha em pintura.',
      pratica: 'Estabeleça um pequeno ritual diário de cuidado próprio que não dependa de ninguém: uma caminhada, uma refeição preparada com atenção, ou 10 minutos de silêncio. Isso fortalece o Guardião sem silenciar o Amante.'
    }
  },
  {
    id: 'guardiao_alquimista',
    natureza: 'Corpo e Sentido',
    eixoA: { key: 'guardiao', name: 'Guardião' },
    eixoB: { key: 'alquimista', name: 'Alquimista' },
    isOpposite: false,
    whenAStronger: {
      titulo: 'A forma que resiste à transformação',
      sombra: 'Quando o Guardião domina sobre o Alquimista, a estrutura se torna tão sólida que não permite a rachadura por onde a luz entra. A ordem repele a incerteza, o concreto resiste à semente. Você pode perceber isso como aversão a questionamentos profundos, desconforto com ambiguidade, ou sensação de que "as coisas são como são".',
      regulacao: 'O caminho é permitir que a dúvida seja hóspede, não inimiga. A transformação não destrói a estrutura — ela a renova. O Guardião mais sábio é aquele que sabe reconstruir.',
      pratica: 'Uma vez por semana, sente-se em silêncio e faça uma pergunta sem buscar resposta: "O que estou evitando transformar?" Não force nada. Apenas escute o que surge.'
    },
    whenBStronger: {
      titulo: 'O espírito sem corpo',
      sombra: 'Quando o Alquimista domina sobre o Guardião, vive-se no reino do significado enquanto o corpo e a vida prática são negligenciados. A sabedoria não encontra chão, as ideias não viram ação. Você pode perceber isso como dificuldade com rotinas básicas, negligência da saúde, ou a sensação de que "entendo tudo mas não consigo fazer".',
      regulacao: 'O caminho é encarnar a sabedoria. Cada insight que não se torna gesto é uma semente que morre na mão. O corpo é o primeiro laboratório do Alquimista.',
      pratica: 'Para cada reflexão profunda que tiver, comprometa-se com uma ação concreta e pequena que a expresse. A sabedoria encarnada é a única que transforma de verdade.'
    }
  },
  {
    id: 'peregrino_amante',
    natureza: 'Tempo e Sentimento',
    eixoA: { key: 'peregrino', name: 'Peregrino' },
    eixoB: { key: 'amante', name: 'Amante' },
    isOpposite: false,
    whenAStronger: {
      titulo: 'O horizonte que não cabe no abraço',
      sombra: 'Quando o Peregrino domina sobre o Amante, a liberdade se confunde com evitação do vínculo. O próximo passo é sempre mais atraente do que a presença. O Amante interior — que precisa de raiz afetiva — começa a se atrofiar. Você pode perceber isso como dificuldade de compromisso, medo de perder liberdade ao se conectar, ou relações intensas mas breves.',
      regulacao: 'O caminho é descobrir que os vínculos profundos não são âncoras — são asas compartilhadas. O Peregrino mais livre é aquele que sabe para onde voltar.',
      pratica: 'Escolha uma pessoa importante e faça algo por ela que exija permanência: uma escuta longa, um projeto juntos, uma presença sem pressa de ir. Observe como o vínculo pode ser expansão, não prisão.'
    },
    whenBStronger: {
      titulo: 'O amor que corta as asas',
      sombra: 'Quando o Amante domina sobre o Peregrino, o medo de perder o outro pode paralisar o movimento. O amor vira grade, a conexão se torna dependência, e o Peregrino interior — que precisa de espaço e descoberta — começa a definhar. Você pode perceber isso como ciúme, dificuldade de fazer coisas sozinho(a), ou a sensação de que sair é trair.',
      regulacao: 'O caminho é confiar que o amor verdadeiro sobrevive à distância. Os vínculos mais fortes são aqueles que permitem que cada um caminhe sua jornada — e se encontre de novo com histórias para contar.',
      pratica: 'Faça algo novo sozinho(a) esta semana — uma saída, um curso, uma exploração. Ao voltar, compartilhe o que viveu. O amor se nutre do que cada um traz de fora.'
    }
  },
  {
    id: 'peregrino_alquimista',
    natureza: 'Tempo e Sentido',
    eixoA: { key: 'peregrino', name: 'Peregrino' },
    eixoB: { key: 'alquimista', name: 'Alquimista' },
    isOpposite: false,
    whenAStronger: {
      titulo: 'A superfície que brilha sem profundidade',
      sombra: 'Quando o Peregrino domina sobre o Alquimista, o movimento se torna tão rápido que não permite a decantação. As experiências se acumulam sem se transformar em sabedoria. O Alquimista interior — que precisa de pausa para transmutar — não encontra silêncio. Você pode perceber isso como sensação de viver muito mas compreender pouco, ou inquietude existencial apesar de muitas experiências.',
      regulacao: 'O caminho é parar tempo suficiente para que a alquimia aconteça. A transformação precisa de fogo, mas também de tempo. Nem toda viagem é para fora — as mais profundas acontecem quando você fica.',
      pratica: 'Ao final de cada experiência significativa, dedique tempo para escrever: "O que isso me ensinou sobre mim?" Não continue até que a resposta tenha profundidade. Esse é o trabalho do Alquimista.'
    },
    whenBStronger: {
      titulo: 'A profundidade que paralisa o passo',
      sombra: 'Quando o Alquimista domina sobre o Peregrino, a busca por sentido pode se tornar paralisia. A reflexão substitui a ação, o pensamento bloqueia o primeiro passo. O Peregrino interior — que precisa de estrada e vento — fica preso no labirinto da mente. Você pode perceber isso como overthinking, procrastinação filosófica, ou a sensação de que precisa entender tudo antes de agir.',
      regulacao: 'O caminho é confiar que o sentido às vezes só aparece em movimento. Nem toda pergunta precisa de resposta antes do primeiro passo. Muitas vezes, a estrada ensina o que a mente não consegue alcançar.',
      pratica: 'Escolha algo que você tem adiado "até entender melhor" e faça hoje. Sem pensar demais. Observe que a compreensão pode nascer do gesto, não antes dele.'
    }
  }
];

// ====== DUPLAS COMO INDICADORES DE INTEGRAÇÃO ======
export const dualInterpretations = {
  visionario: {
    name: 'Visionário',
    eixos: ['alquimista', 'peregrino'],
    eixoNames: ['Alquimista', 'Peregrino'],
    whenActive: 'O Visionário está desperto em você — sentido e movimento caminham juntos. Sua capacidade de enxergar possibilidades futuras está ativa, unindo profundidade com ousadia.',
    whenSilent: 'O Visionário descansa — sentido e movimento ainda buscam se encontrar. A integração entre a profundidade do Alquimista e a coragem do Peregrino é uma fronteira a explorar.'
  },
  encontro: {
    name: 'Peregrino do Encontro',
    eixos: ['peregrino', 'amante'],
    eixoNames: ['Peregrino', 'Amante'],
    whenActive: 'O Peregrino do Encontro está vivo — você encontra os outros sem se perder. Jornada e vínculo se alimentam mutuamente em você.',
    whenSilent: 'O Peregrino do Encontro descansa — caminho e coração ainda buscam se alinhar. Liberdade e pertencimento pedem uma ponte mais forte entre si.'
  },
  guerreiro: {
    name: 'Guerreiro',
    eixos: ['peregrino', 'guardiao'],
    eixoNames: ['Peregrino', 'Guardião'],
    whenActive: 'O Guerreiro está presente — movimento e estrutura se complementam. Você sabe quando avançar e quando sustentar, unindo coragem com firmeza.',
    whenSilent: 'O Guerreiro descansa — ação e proteção ainda buscam equilíbrio. O desafio entre mudar e manter é uma tensão criativa a ser trabalhada.'
  },
  curador: {
    name: 'Curador',
    eixos: ['amante', 'guardiao'],
    eixoNames: ['Amante', 'Guardião'],
    whenActive: 'O Curador está vivo — afeto e cuidado estão integrados em você. Sua capacidade de acolher e proteger ao mesmo tempo cria um espaço seguro para si e para os outros.',
    whenSilent: 'O Curador descansa — sentimento e estrutura ainda se buscam. A ponte entre vulnerabilidade e firmeza é um caminho de amadurecimento.'
  },
  guardiao_sabio: {
    name: 'Guardião Sábio',
    eixos: ['guardiao', 'alquimista'],
    eixoNames: ['Guardião', 'Alquimista'],
    whenActive: 'O Guardião Sábio está presente — forma e sentido se unem. Sua estabilidade ganha profundidade, e sua sabedoria encontra um corpo para se expressar.',
    whenSilent: 'O Guardião Sábio descansa — corpo e espírito ainda procuram se integrar. A ponte entre o concreto e o sagrado é uma jornada em construção.'
  },
  poeta: {
    name: 'Poeta',
    eixos: ['amante', 'alquimista'],
    eixoNames: ['Amante', 'Alquimista'],
    whenActive: 'O Poeta está vivo — amor e sentido dançam juntos. Você encontra beleza no significado e significado na beleza. Essa integração traz profundidade às suas conexões.',
    whenSilent: 'O Poeta descansa — sentimento e sentido ainda buscam se encontrar. A ponte entre amar e compreender pede atenção e cultivo.'
  }
};

// ====== TRIPLAS COMO INDICADORES DE CONVERGÊNCIA ======
export const tripleInterpretations = {
  profeta: {
    name: 'Profeta',
    eixos: ['peregrino', 'amante', 'alquimista'],
    neglectedAxis: { key: 'guardiao', name: 'Guardião' },
    whenActive: 'O Profeta converge em você — movimento, sentimento e sentido se alinham. Mas atenção: quando três forças se unem, a quarta pode ficar negligenciada. O Guardião (corpo, estrutura, rotina) pede cuidado.',
    whenSilent: 'A convergência profética ainda é uma possibilidade em gestação. Peregrino, Amante e Alquimista buscam um ponto de encontro em você.'
  },
  construtor: {
    name: 'Construtor',
    eixos: ['peregrino', 'guardiao', 'alquimista'],
    neglectedAxis: { key: 'amante', name: 'Amante' },
    whenActive: 'O Construtor converge em você — movimento, estrutura e sentido se complementam. Mas quando três forças se alinham, a quarta pode ficar esquecida. O Amante (afeto, vínculo, entrega) pede presença.',
    whenSilent: 'A convergência construtora ainda amadurece. Peregrino, Guardião e Alquimista buscam harmonia entre si.'
  },
  companheiro: {
    name: 'Companheiro de Jornada',
    eixos: ['peregrino', 'guardiao', 'amante'],
    neglectedAxis: { key: 'alquimista', name: 'Alquimista' },
    whenActive: 'O Companheiro converge em você — movimento, cuidado e afeto caminham juntos. Mas quando três forças se unem, a quarta pode se ausentar. O Alquimista (sentido, transformação, profundidade) pede espaço.',
    whenSilent: 'A convergência do Companheiro ainda está em formação. Peregrino, Guardião e Amante buscam integração.'
  },
  sabio_compassivo: {
    name: 'Sábio Compassivo',
    eixos: ['guardiao', 'amante', 'alquimista'],
    neglectedAxis: { key: 'peregrino', name: 'Peregrino' },
    whenActive: 'O Sábio Compassivo converge em você — proteção, afeto e sentido se abraçam. Mas quando três forças se alinham, a quarta pode ser esquecida. O Peregrino (movimento, renovação, abertura) pede passagem.',
    whenSilent: 'A convergência do Sábio Compassivo ainda se constrói. Guardião, Amante e Alquimista buscam seu ponto de encontro.'
  }
};

// ====== FUNÇÃO PRINCIPAL: CALCULAR DINÂMICAS ATIVAS ======
export const calculateDynamics = (result) => {
  const { mainArchetypes } = result;
  const dualCombinations = result.dualCombinations || {};
  const tripleCombinations = result.tripleCombinations || {};

  // 1. Calcular tensões entre eixos raiz
  const activeTensions = [];
  
  tensionPairs.forEach(pair => {
    const scoreA = mainArchetypes[pair.eixoA.key] || 0;
    const scoreB = mainArchetypes[pair.eixoB.key] || 0;
    const diff = Math.abs(scoreA - scoreB);
    
    if (diff >= TENSION_MODERATE) {
      const tension = {
        ...pair,
        scoreA,
        scoreB,
        diff,
        intensity: diff >= TENSION_STRONG ? 'forte' : 'moderada',
        direction: scoreA > scoreB ? 'A' : 'B',
        reading: scoreA > scoreB ? pair.whenAStronger : pair.whenBStronger,
        dominant: scoreA > scoreB ? pair.eixoA : pair.eixoB,
        shadowed: scoreA > scoreB ? pair.eixoB : pair.eixoA
      };
      activeTensions.push(tension);
    }
  });
  
  // Ordenar por intensidade (maior diferença primeiro)
  activeTensions.sort((a, b) => b.diff - a.diff);

  // 2. Interpretar duplas
  const dualReadings = [];
  Object.entries(dualInterpretations).forEach(([key, dual]) => {
    const score = dualCombinations[key];
    if (score === undefined) return;
    
    const isActive = score >= 6;
    const isSilent = score < 4;
    
    if (isActive || isSilent) {
      dualReadings.push({
        key,
        ...dual,
        score,
        maxScore: 10,
        status: isActive ? 'ativo' : 'silencioso',
        reading: isActive ? dual.whenActive : dual.whenSilent
      });
    }
  });

  // 3. Interpretar triplas
  const tripleReadings = [];
  Object.entries(tripleInterpretations).forEach(([key, triple]) => {
    const score = tripleCombinations[key];
    if (score === undefined) return;
    
    const isActive = score >= 12;
    const isSilent = score < 8;
    
    if (isActive || isSilent) {
      tripleReadings.push({
        key,
        ...triple,
        score,
        maxScore: 20,
        status: isActive ? 'ativo' : 'silencioso',
        reading: isActive ? triple.whenActive : triple.whenSilent
      });
    }
  });

  return {
    tensions: activeTensions.slice(0, 3), // Max 3 tensões
    dualReadings,
    tripleReadings,
    isBalanced: activeTensions.length === 0,
    isCoracaoInteiro: result.isCoracaoInteiro || false
  };
};
